import React, { useEffect } from "react";
import { Handle, Position, NodeProps } from "@xyflow/react";
import { toolsList } from "@packages/utils";
import { useTranslation } from "react-i18next";
import { MindmapNodeToolInfo } from "@packages/api/madlenAPI.schemas";

// Re-import tool types if defined elsewhere, or define here
// For simplicity, let's assume they are accessible here
// If not, import them from index.tsx or a shared types file.

// Interface for the specific fields *we* add to the node data
interface CustomNodeFields {
  label: string;
  description?: string;
  onShowDescription?: () => void;
  toolInfo?: MindmapNodeToolInfo;
}

// // Minimal interface for the NodeProps generic constraint
// export interface MindMapNodeDataBase {
//   label: string; // Must include at least one required field for the constraint?
// }

// Type the props argument loosely to avoid constraint issues
export function MindMapNode({ data }: NodeProps<any>) {
  // Assert the actual type of data based on how nodes are created in index.tsx
  // Combine with Record<string, unknown> for robustness
  const nodeData = data as CustomNodeFields & Record<string, unknown>;
  const { t } = useTranslation();
  const allTools = toolsList.flatMap((category) =>
    category.tools.map((tool) => ({
      id: tool.toolType,
      title: t(tool.title),
      icon: tool.icon,
      color: tool.colors?.light || "from-gray-500 to-gray-600",
      category: t(category.categoryTitle),
    })),
  );

  const handleDescriptionClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering node click (collapse/expand)
    console.log("nodeData", nodeData);
    // Use the asserted nodeData
    if (nodeData?.onShowDescription) {
      nodeData.onShowDescription();
    }
  };
  const toolInfo = nodeData.toolInfo;
  const tool = allTools.find((tool) => tool.id === toolInfo?.toolType);
  const isProcessing = toolInfo?.status === "PROCESSING";

  // Define the pulse animation class
  const pulseAnimationClass = isProcessing
    ? "animate-pulse border-opacity-80 border-blue-500 shadow-lg shadow-blue-500/40"
    : "";

  return (
    // Increased padding slightly to accommodate indicators
    <div
      className={`p-3 shadow-md rounded-md relative flex items-center justify-between w-44 ${
        tool
          ? tool.color + " bg-opacity-60"
          : "border-blue-500 dark:border-blue-500 bg-gray-200 dark:bg-gray-800"
      } ${pulseAnimationClass} ${!isProcessing ? "border-2" : ""}`}
      style={
        isProcessing
          ? {
              boxShadow:
                "0 0 15px 2px rgba(59, 130, 246, 0.5), 0 0 15px 2px rgba(139, 92, 246, 0.5), 0 0 15px 2px rgba(45, 212, 191, 0.5)",
            }
          : {}
      }
    >
      {isProcessing && (
        <div
          className="absolute inset-[-3px] rounded-md -z-10"
          style={{
            background:
              "linear-gradient(45deg, #8b5cf6, #3b82f6, #2dd4bf, #8b5cf6)",
            backgroundSize: "300% 300%",
            animation:
              "gradient-rotate 3s ease infinite, glow-pulse 2s ease-in-out infinite",
          }}
        />
      )}

      {/* Node Content Area */}
      <div className="flex flex-col items-start flex-grow mr-2">
        {/* Label */}
        {/* Access label from asserted nodeData */}
        <div className="text-base font-medium">{nodeData.label}</div>
      </div>

      {/* Description Button */}
      {/* Access description from asserted nodeData */}
      {nodeData.description && (
        <button
          onClick={handleDescriptionClick}
          className="btn btn-xs btn-circle btn-info btn-outline p-0 leading-none flex items-center justify-center"
          aria-label="Show description"
          style={{ width: "1.2rem", height: "1.2rem", minHeight: "1.2rem" }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </button>
      )}

      {/* === Target Handles === */}
      <Handle
        type="target"
        position={Position.Top}
        id="top-target"
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-target"
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-target"
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-target"
        className="!bg-transparent invisible"
        isConnectable={false}
      />

      <Handle
        type="target"
        position={Position.Top}
        id="top-right-target"
        style={{ left: "75%", top: 0 }}
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-right-target"
        style={{ left: "75%", bottom: 0 }}
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-left-target"
        style={{ left: "25%", bottom: 0 }}
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="top-left-target"
        style={{ left: "25%", top: 0 }}
        className="!bg-transparent invisible"
        isConnectable={false}
      />

      {/* === Source Handles === */}
      <Handle
        type="source"
        position={Position.Top}
        id="top-source"
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-source"
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-source"
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-source"
        className="!bg-transparent invisible"
        isConnectable={false}
      />

      <Handle
        type="source"
        position={Position.Top}
        id="top-right-source"
        style={{ left: "75%", top: 0 }}
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-right-source"
        style={{ left: "75%", bottom: 0 }}
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-left-source"
        style={{ left: "25%", bottom: 0 }}
        className="!bg-transparent invisible"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-left-source"
        style={{ left: "25%", top: 0 }}
        className="!bg-transparent invisible"
        isConnectable={false}
      />
    </div>
  );
}

// Default export might be needed if dynamically imported elsewhere, but keep named export too.
// export default MindMapNode; // Let's avoid default export for now
